import React, { useState, useEffect } from "react";

import {
  Database,
  Users,
  Shield,
  LogOut,
  CheckCircle,
  Edit3,
  Save,
  Settings,
  UserPlus,
  BarChart3,
  Key,
  ShieldCheck,
  BookOpen,
  Map,
  MapPin,
  Calendar,
  ClipboardList,
  Megaphone,
} from "lucide-react";
import { AUTH_URL, getToken, authFetch } from "./lib/api";
import { ToastHost } from "./lib/toast";
import { EmailIntelligencePortal } from "./portals/EmailIntelligencePortal";
import { MarketingPortal } from "./portals/MarketingPortal";
import { ArticleGeneratorPortal } from "./portals/ArticleGeneratorPortal";
import { SLMPortal } from "./portals/SLMPortal";
import { PrivateLLMChat } from "./portals/PrivateLLMChat";
import { DataMapPortal } from "./portals/DataMapPortal";

import { VideoPortal } from "./portals/VideoPortal";
import { DistributionPortal } from "./portals/DistributionPortal";
import { PodcastPortal } from "./portals/PodcastPortal";

import { RAGApiKeys } from "./portals/RAGApiKeys";
import { RAGAuditLog } from "./portals/RAGAuditLog";

import { RAGChat } from "./portals/RAGChat";
import { UserManagement } from "./components/UserManagement";
import { PASPortal } from "./portals/PASPortal";
import { PatientAftercarePortal } from "./portals/PatientAftercarePortal";
import { SystemBanner } from "./components/SystemBanner";
import { VoiceIQPortal } from "./portals/VoiceIQPortal";
import { TelegramBotPortal } from "./portals/TelegramBotPortal";
import { BuildIQPortal } from "./portals/BuildIQPortal";
import { SERVICE_META } from "./lib/serviceMeta";
import { TopicsManagement } from "./admin/TopicsManagement";
import { WalkPathManagement } from "./admin/WalkPathManagement";
import { BroadcastsPanel } from "./admin/BroadcastsPanel";
import { FacilitySettingsTab } from "./admin/FacilitySettingsTab";
import { PrecinctCaptainsPanel } from "./admin/PrecinctCaptainsPanel";
import { CalendarFeedsPanel } from "./admin/CalendarFeedsPanel";
import { ReferenceDataPanel } from "./admin/ReferenceDataPanel";
import { SurveysPanel } from "./admin/Surveys";
import { UsageAnalyticsDashboard } from "./analytics/UsageAnalyticsDashboard";
import { LoginPage } from "./screens/Auth";
import { isAllowedWebhookUrl } from "./lib/utils";

// ═══════════════════════════════════════════════════════════════════
// SECURITY HELPERS
// ═══════════════════════════════════════════════════════════════════

// Toast system (showToast + ToastHost) lives in ./lib/toast.

// ═══════════════════════════════════════════════════════════════════
// RAG CHATBOT UTILITIES
// ═══════════════════════════════════════════════════════════════════

const Dashboard = ({ facilities, onNavigate }) => {
  if (!facilities.length) {
    return (
      <div className="text-center py-20">
        <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h2 className="text-lg font-medium text-slate-700">
          No Services Assigned
        </h2>
        <p className="text-sm text-slate-500 mt-2">
          Contact your facility administrator to get access to AI services.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {facilities.map((fac) => (
        <div
          key={fac.facility_id}
          className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden"
        >
          <div className="px-6 py-4 bg-gradient-to-r from-slate-800 to-slate-700 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-white">
                {fac.facility_name}
              </h2>
              <p className="text-sm text-slate-300">
                {fac.facility_type} | {fac.vertical_id}
              </p>
            </div>
            {fac.is_facility_admin && (
              <button
                onClick={() => onNavigate("facility-admin", fac)}
                className="flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-white/10 text-white hover:bg-white/20 transition-colors"
              >
                <Shield className="w-4 h-4 mr-1.5" />
                Admin
              </button>
            )}
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {fac.services.map((svc) => {
              const meta = SERVICE_META[svc.service_type] || {};
              const Icon = meta.icon || Database;
              const colorMap = {
                blue: "border-blue-300 bg-blue-50",
                purple: "border-purple-300 bg-purple-50",
                amber: "border-amber-300 bg-amber-50",
                emerald: "border-emerald-300 bg-emerald-50",
                rose: "border-rose-300 bg-rose-50",
                cyan: "border-cyan-300 bg-cyan-50",
                slate: "border-slate-300 bg-slate-50",
                teal: "border-teal-300 bg-teal-50",
                indigo: "border-indigo-300 bg-indigo-50",
                orange: "border-orange-300 bg-orange-50",
              };
              const textMap = {
                blue: "text-blue-700",
                purple: "text-purple-700",
                amber: "text-amber-700",
                emerald: "text-emerald-700",
                rose: "text-rose-700",
                cyan: "text-cyan-700",
                slate: "text-slate-700",
                teal: "text-teal-700",
                indigo: "text-indigo-700",
                orange: "text-orange-700",
              };
              return (
                <button
                  key={svc.service_type}
                  onClick={() => onNavigate(svc.service_type, fac)}
                  className={`p-5 rounded-xl border-2 ${colorMap[meta.color] || "border-slate-200 bg-slate-50"} text-left hover:shadow-md transition-shadow`}
                >
                  <Icon
                    className={`w-8 h-8 ${textMap[meta.color] || "text-slate-600"} mb-3`}
                  />
                  <h3
                    className={`font-semibold ${textMap[meta.color] || "text-slate-700"}`}
                  >
                    {meta.label}
                  </h3>
                  <p className="text-xs text-slate-500 mt-1">{meta.desc}</p>
                  <div className="flex items-center mt-3 space-x-2">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${svc.service_status === "active" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}
                    >
                      {svc.service_status}
                    </span>
                    <span className="text-xs text-slate-400">
                      Role: {svc.access_role}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// BRIEFINGS PANEL
// ═══════════════════════════════════════════════════════════════════

const AdminPanel = ({ facility, user }) => {
  const [subTab, setSubTab] = useState("users");

  const subTabs = [
    { id: "users", label: "Users", icon: Users },
    { id: "walks", label: "Routes", icon: Map },
    { id: "captains", label: "Captains", icon: UserPlus },
    { id: "topics", label: "Topics", icon: BookOpen },
    { id: "analytics", label: "Analytics", icon: BarChart3 },
    { id: "broadcasts", label: "Broadcasts", icon: Megaphone },
    { id: "calendar-feeds", label: "Calendar Feeds", icon: Calendar },
    { id: "locations", label: "Locations & People", icon: MapPin },
    { id: "surveys", label: "Surveys", icon: ClipboardList },
    { id: "api-keys", label: "API Keys", icon: Key },
    { id: "audit-log", label: "Audit Log", icon: ShieldCheck },
    { id: "settings", label: "Settings", icon: Settings },
  ];

  return (
    <div className="space-y-4">
      <div className="flex space-x-1 bg-slate-100 rounded-lg p-1 overflow-x-auto">
        {subTabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
              subTab === t.id
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            <t.icon className="w-4 h-4 mr-1.5" />
            {t.label}
          </button>
        ))}
      </div>

      {subTab === "users" && <UserManagement facility={facility} />}
      {subTab === "walks" && <WalkPathManagement facility={facility} />}
      {subTab === "captains" && <PrecinctCaptainsPanel facility={facility} />}
      {subTab === "topics" && <TopicsManagement facility={facility} />}
      {subTab === "analytics" && (
        <UsageAnalyticsDashboard facility={facility} />
      )}
      {subTab === "broadcasts" && <BroadcastsPanel facility={facility} />}
      {subTab === "calendar-feeds" && (
        <CalendarFeedsPanel facility={facility} />
      )}
      {subTab === "locations" && <ReferenceDataPanel facility={facility} />}
      {subTab === "surveys" && (
        <SurveysPanel facility={facility} kbRole="admin" />
      )}
      {subTab === "api-keys" && <RAGApiKeys facility={facility} user={user} />}
      {subTab === "audit-log" && <RAGAuditLog facility={facility} />}
      {subTab === "settings" && <FacilitySettingsTab facility={facility} />}
    </div>
  );
};

const AdminFacilityConfig = () => {
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState({}); // facilityId -> draft object
  const [saving, setSaving] = useState(null);
  const [saved, setSaved] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    authFetch("/admin/rag/service-config")
      .then((data) => {
        if (Array.isArray(data)) setFacilities(data);
        else setError(data.error || "Failed to load");
      })
      .catch(() => setError("Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  const startEdit = (fac) => {
    setEditing((prev) => ({
      ...prev,
      [fac.facility_id]: {
        // Identity
        agent_name: fac.agent_name || "",
        logo_url: fac.logo_url || fac.config?.logo_url || "",
        system_prompt: fac.system_prompt || "",
        // Security
        allowed_origins: fac.allowed_origins || "",
        // Endpoints
        webhook_url: fac.config?.webhook_url || "",
        // Retrieval
        top_k: fac.config?.top_k ?? 5,
        pinecone_namespace: fac.config?.pinecone_namespace || "",
        pinecone_index: fac.config?.pinecone_index || "",
        // Workflows
        n8n_workflow_id: fac.config?.n8n_workflow_id || "",
        n8n_ingest_workflow_id: fac.config?.n8n_ingest_workflow_id || "",
      },
    }));
  };

  const cancelEdit = (facilityId) => {
    setEditing((prev) => {
      const next = { ...prev };
      delete next[facilityId];
      return next;
    });
  };

  const updateDraft = (facilityId, key, value) => {
    setEditing((prev) => ({
      ...prev,
      [facilityId]: { ...prev[facilityId], [key]: value },
    }));
  };

  const saveConfig = async (facilityId) => {
    const draft = editing[facilityId];
    if (draft.webhook_url && !isAllowedWebhookUrl(draft.webhook_url)) {
      setError("Webhook URL must be HTTPS");
      return;
    }
    setSaving(facilityId);
    setError(null);
    try {
      const result = await authFetch("/admin/rag/service-config", {
        method: "PUT",
        body: JSON.stringify({
          facility_id: facilityId,
          agent_name: draft.agent_name,
          logo_url: draft.logo_url || null,
          system_prompt: draft.system_prompt,
          allowed_origins: draft.allowed_origins,
          webhook_url: draft.webhook_url,
          top_k: Number(draft.top_k) || 5,
          pinecone_namespace: draft.pinecone_namespace,
          pinecone_index: draft.pinecone_index,
          n8n_workflow_id: draft.n8n_workflow_id,
          n8n_ingest_workflow_id: draft.n8n_ingest_workflow_id,
        }),
      });
      if (result.ok) {
        setFacilities((prev) =>
          prev.map((f) =>
            f.facility_id === facilityId
              ? {
                  ...f,
                  agent_name: draft.agent_name,
                  logo_url: draft.logo_url || null,
                  system_prompt: draft.system_prompt,
                  allowed_origins: draft.allowed_origins,
                  config: {
                    ...f.config,
                    logo_url: draft.logo_url || null,
                    webhook_url: draft.webhook_url,
                    top_k: Number(draft.top_k) || 5,
                    pinecone_namespace: draft.pinecone_namespace,
                    pinecone_index: draft.pinecone_index,
                    n8n_workflow_id: draft.n8n_workflow_id,
                    n8n_ingest_workflow_id: draft.n8n_ingest_workflow_id,
                  },
                }
              : f,
          ),
        );
        cancelEdit(facilityId);
        setSaved(facilityId);
        setTimeout(() => setSaved(null), 3000);
      } else {
        setError(result.error || "Save failed");
      }
    } catch {
      setError("Save failed");
    } finally {
      setSaving(null);
    }
  };

  const DEFAULT_WEBHOOK = `${new URL(AUTH_URL, window.location.href).origin.replace(/\/auth$/, "")}/webhook/lidy`;

  if (loading)
    return <div className="text-slate-500 text-sm py-8">Loading...</div>;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-800">
          RAG Service Configuration
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Manage per-facility chatbot persona, system prompt, CORS origins,
          webhook URL, and retrieval settings.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded text-sm">
          {error}
        </div>
      )}

      <div className="space-y-3">
        {facilities.length === 0 && (
          <div className="bg-white rounded-lg border border-slate-200 px-6 py-8 text-center text-slate-400 text-sm">
            No facilities found
          </div>
        )}
        {facilities.map((fac) => {
          const isEditing = fac.facility_id in editing;
          const draft = editing[fac.facility_id] || {};
          return (
            <div
              key={fac.facility_id}
              className="bg-white rounded-lg border border-slate-200 overflow-hidden"
            >
              {/* Header row */}
              <div className="px-5 py-3 flex items-center justify-between gap-4 bg-slate-50 border-b border-slate-100">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-medium text-slate-800 text-sm">
                    {fac.facility_name}
                  </span>
                  <span className="text-xs text-slate-400 font-mono shrink-0">
                    {fac.facility_id}
                  </span>
                  {fac.service_status && (
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${
                        fac.service_status === "active"
                          ? "bg-green-100 text-green-700"
                          : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {fac.service_status}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {saved === fac.facility_id && (
                    <span className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> Saved
                    </span>
                  )}
                  {isEditing ? (
                    <>
                      <button
                        onClick={() => saveConfig(fac.facility_id)}
                        disabled={saving === fac.facility_id}
                        className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                      >
                        {saving === fac.facility_id ? (
                          "Saving…"
                        ) : (
                          <>
                            <Save className="w-3 h-3" /> Save
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => cancelEdit(fac.facility_id)}
                        className="px-3 py-1.5 text-slate-500 text-xs rounded hover:bg-slate-100"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => startEdit(fac)}
                      className="text-xs text-slate-500 hover:text-blue-600 flex items-center gap-1 px-2 py-1.5 rounded hover:bg-slate-100"
                    >
                      <Edit3 className="w-3 h-3" /> Edit
                    </button>
                  )}
                </div>
              </div>

              {/* Field rows */}
              {isEditing ? (
                <div className="p-5 space-y-5">
                  {/* ── Identity ── */}
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">
                      Identity
                    </p>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Persona Name
                        </label>
                        <input
                          type="text"
                          value={draft.agent_name ?? ""}
                          onChange={(e) =>
                            updateDraft(
                              fac.facility_id,
                              "agent_name",
                              e.target.value,
                            )
                          }
                          placeholder="e.g. Lidy"
                          className="w-full text-sm border border-slate-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <p className="text-xs text-slate-400 mt-1">
                          Chatbot name shown in widget header
                        </p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Logo URL
                        </label>
                        <div className="flex items-center gap-2">
                          {draft.logo_url && (
                            <img
                              src={draft.logo_url}
                              alt="Logo preview"
                              className="h-8 w-8 object-contain rounded border border-slate-200 bg-slate-50 flex-shrink-0"
                              onError={(e) =>
                                (e.currentTarget.style.display = "none")
                              }
                            />
                          )}
                          <input
                            type="url"
                            value={draft.logo_url ?? ""}
                            onChange={(e) =>
                              updateDraft(
                                fac.facility_id,
                                "logo_url",
                                e.target.value,
                              )
                            }
                            placeholder="https://example.com/logo.png"
                            className="w-full text-sm border border-slate-300 rounded px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <p className="text-xs text-slate-400 mt-1">
                          Replaces the Hyve logo in FieldIQ. Leave blank to use
                          default.
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Top K Results
                        </label>
                        <input
                          type="number"
                          min={1}
                          max={20}
                          value={draft.top_k ?? 5}
                          onChange={(e) =>
                            updateDraft(
                              fac.facility_id,
                              "top_k",
                              e.target.value,
                            )
                          }
                          className="w-full text-sm border border-slate-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <p className="text-xs text-slate-400 mt-1">
                          Document chunks retrieved per query (1–20)
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* ── Endpoints ── */}
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">
                      Endpoints
                    </p>
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Chat Webhook URL
                        </label>
                        <input
                          type="url"
                          value={draft.webhook_url ?? ""}
                          onChange={(e) =>
                            updateDraft(
                              fac.facility_id,
                              "webhook_url",
                              e.target.value,
                            )
                          }
                          placeholder={DEFAULT_WEBHOOK}
                          className="w-full text-sm border border-slate-300 rounded px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <p className="text-xs text-slate-400 mt-1">
                          n8n webhook URL for chat queries. Leave blank for
                          default:{" "}
                          <span className="font-mono">{DEFAULT_WEBHOOK}</span>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* ── Vector Store ── */}
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">
                      Vector Store
                    </p>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Pinecone Namespace
                        </label>
                        <input
                          type="text"
                          value={draft.pinecone_namespace ?? ""}
                          onChange={(e) =>
                            updateDraft(
                              fac.facility_id,
                              "pinecone_namespace",
                              e.target.value,
                            )
                          }
                          placeholder="e.g. AZLD4v1"
                          className="w-full text-sm border border-slate-300 rounded px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <p className="text-xs text-slate-400 mt-1">
                          Pinecone namespace for this facility's KB
                        </p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Pinecone Index
                        </label>
                        <input
                          type="text"
                          value={draft.pinecone_index ?? ""}
                          onChange={(e) =>
                            updateDraft(
                              fac.facility_id,
                              "pinecone_index",
                              e.target.value,
                            )
                          }
                          placeholder="e.g. gop"
                          className="w-full text-sm border border-slate-300 rounded px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <p className="text-xs text-slate-400 mt-1">
                          Pinecone index name
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* ── Workflows ── */}
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">
                      Workflows
                    </p>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          n8n Chat Workflow ID
                        </label>
                        <input
                          type="text"
                          value={draft.n8n_workflow_id ?? ""}
                          onChange={(e) =>
                            updateDraft(
                              fac.facility_id,
                              "n8n_workflow_id",
                              e.target.value,
                            )
                          }
                          placeholder="e.g. abc-123-def"
                          className="w-full text-sm border border-slate-300 rounded px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <p className="text-xs text-slate-400 mt-1">
                          n8n workflow ID handling chat queries
                        </p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          n8n Ingest Workflow ID
                        </label>
                        <input
                          type="text"
                          value={draft.n8n_ingest_workflow_id ?? ""}
                          onChange={(e) =>
                            updateDraft(
                              fac.facility_id,
                              "n8n_ingest_workflow_id",
                              e.target.value,
                            )
                          }
                          placeholder="e.g. xyz-456-ghi"
                          className="w-full text-sm border border-slate-300 rounded px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <p className="text-xs text-slate-400 mt-1">
                          n8n workflow ID for document ingestion
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* ── Security ── */}
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">
                      Security
                    </p>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        CORS Origins
                      </label>
                      <input
                        type="text"
                        value={draft.allowed_origins ?? ""}
                        onChange={(e) =>
                          updateDraft(
                            fac.facility_id,
                            "allowed_origins",
                            e.target.value,
                          )
                        }
                        placeholder="https://example.com,https://other.com"
                        className="w-full text-sm border border-slate-300 rounded px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-slate-400 mt-1">
                        Comma-separated allowed origins for the embed widget
                      </p>
                    </div>
                  </div>

                  {/* ── System Prompt ── */}
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">
                      System Prompt
                    </p>
                    <textarea
                      value={draft.system_prompt ?? ""}
                      onChange={(e) =>
                        updateDraft(
                          fac.facility_id,
                          "system_prompt",
                          e.target.value,
                        )
                      }
                      rows={6}
                      placeholder="You are a helpful assistant for {facility_name}..."
                      className="w-full text-sm border border-slate-300 rounded px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      Instructions given to the AI before each conversation
                    </p>
                  </div>
                </div>
              ) : (
                <div className="px-5 py-4 space-y-3 text-sm">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3">
                    <div>
                      <span className="text-xs text-slate-400 block">
                        Persona
                      </span>
                      <span className="text-slate-700 font-medium">
                        {fac.agent_name || (
                          <span className="text-slate-400 italic">—</span>
                        )}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-slate-400 block">
                        Top K
                      </span>
                      <span className="text-slate-700 font-medium">
                        {fac.config?.top_k ?? (
                          <span className="text-slate-400 italic">
                            5 (default)
                          </span>
                        )}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-slate-400 block">
                        Namespace
                      </span>
                      <span className="text-slate-700 font-mono text-xs">
                        {fac.config?.pinecone_namespace || (
                          <span className="text-slate-400 italic">—</span>
                        )}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-slate-400 block">
                        Index
                      </span>
                      <span className="text-slate-700 font-mono text-xs">
                        {fac.config?.pinecone_index || (
                          <span className="text-slate-400 italic">—</span>
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3">
                    <div>
                      <span className="text-xs text-slate-400 block">
                        Chat Webhook
                      </span>
                      <span className="text-slate-700 font-mono text-xs truncate block">
                        {fac.config?.webhook_url ? (
                          <span className="text-blue-600">
                            {fac.config.webhook_url}
                          </span>
                        ) : (
                          <span className="text-slate-400 italic">default</span>
                        )}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-slate-400 block">
                        CORS Origins
                      </span>
                      <span className="text-slate-700 font-mono text-xs truncate block">
                        {fac.allowed_origins || (
                          <span className="text-slate-400 italic">—</span>
                        )}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-slate-400 block">
                        Chat Workflow ID
                      </span>
                      <span className="text-slate-700 font-mono text-xs">
                        {fac.config?.n8n_workflow_id || (
                          <span className="text-slate-400 italic">—</span>
                        )}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-slate-400 block">
                        Ingest Workflow ID
                      </span>
                      <span className="text-slate-700 font-mono text-xs">
                        {fac.config?.n8n_ingest_workflow_id || (
                          <span className="text-slate-400 italic">—</span>
                        )}
                      </span>
                    </div>
                  </div>
                  <div>
                    <span className="text-xs text-slate-400 block">
                      System Prompt
                    </span>
                    <span className="text-slate-600 text-xs line-clamp-2">
                      {fac.system_prompt || (
                        <span className="text-slate-400 italic">—</span>
                      )}
                    </span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// MAIN APP
// ═══════════════════════════════════════════════════════════════════

const MSG_DISMISSED_KEY = "hyve_dismissed_msgs";

const getDismissed = () => {
  try {
    return new Set(
      JSON.parse(sessionStorage.getItem(MSG_DISMISSED_KEY) || "[]"),
    );
  } catch {
    return new Set();
  }
};

const saveDismissed = (set) => {
  sessionStorage.setItem(MSG_DISMISSED_KEY, JSON.stringify([...set]));
};

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(getToken());
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState("dashboard");
  const [activeFacility, setActiveFacility] = useState(null);
  const [systemMessages, setSystemMessages] = useState([]);
  const [dismissed, setDismissed] = useState(getDismissed);
  const [pendingEmailCid, setPendingEmailCid] = useState(null);

  // Check auth on mount
  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    authFetch("/me")
      .then((data) => {
        if (data.user) {
          setUser(data.user);
        } else {
          localStorage.removeItem("hyveai_token");
          setToken(null);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [token]);

  // Fetch user's services after login
  useEffect(() => {
    if (!user) return;
    authFetch("/my-services").then((data) => {
      if (Array.isArray(data)) setFacilities(data);
    });
  }, [user]);

  // Handle M365 consent callback redirect
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("email_intel_step") === "3") {
      const cid = params.get("cid");
      if (cid) setPendingEmailCid(cid);
      setPage("email_intelligence");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [user]);

  // Fetch system messages once logged in
  useEffect(() => {
    if (!user) return;
    fetch(`${AUTH_URL}/system-messages`)
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) setSystemMessages(data);
      })
      .catch(() => {});
  }, [user]);

  const dismissMessage = (id) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(id);
      saveDismissed(next);
      return next;
    });
  };

  const handleLogin = (u, t) => {
    setUser(u);
    setToken(t);
  };
  const handleLogout = () => {
    localStorage.removeItem("hyveai_token");
    setUser(null);
    setToken(null);
    setFacilities([]);
  };

  const navigateToService = (serviceType, facility) => {
    setActiveFacility(facility);
    setPage(serviceType);
  };

  if (loading)
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center text-slate-500">
        Loading...
      </div>
    );
  if (!user) return <LoginPage onLogin={handleLogin} />;

  const facilityMessages = activeFacility
    ? systemMessages.filter(
        (m) =>
          m.scope === "facility" &&
          m.facility_id === activeFacility.facility_id &&
          !dismissed.has(m.id),
      )
    : [];

  const globalMessages = systemMessages.filter(
    (m) => m.scope === "global" && !dismissed.has(m.id),
  );

  const isActiveFacilityAdmin =
    user.role === "admin" || activeFacility?.is_facility_admin === true;

  // ─── Telegram Bot Portal ──────────────────────────────────────────────────

  const renderPage = () => {
    switch (page) {
      case "rag":
        return activeFacility ? (
          <RAGChat
            facility={activeFacility}
            user={user}
            systemMessages={facilityMessages}
            onDismissMessage={dismissMessage}
          />
        ) : null;
      case "slm":
        return activeFacility ? (
          <SLMPortal
            facility={activeFacility}
            user={user}
            systemMessages={facilityMessages}
            onDismissMessage={dismissMessage}
          />
        ) : null;
      case "private_llm":
        return activeFacility ? (
          <PrivateLLMChat
            facility={activeFacility}
            user={user}
            systemMessages={facilityMessages}
            onDismissMessage={dismissMessage}
          />
        ) : null;
      case "datamap":
        return activeFacility ? (
          <DataMapPortal
            facility={activeFacility}
            user={user}
            systemMessages={facilityMessages}
            onDismissMessage={dismissMessage}
          />
        ) : null;
      case "podcast":
        return activeFacility ? (
          <PodcastPortal
            facility={activeFacility}
            user={user}
            systemMessages={facilityMessages}
            onDismissMessage={dismissMessage}
          />
        ) : null;
      case "video":
        return activeFacility ? (
          <VideoPortal facility={activeFacility} user={user} />
        ) : null;
      case "distribution":
        return activeFacility ? (
          <DistributionPortal facility={activeFacility} user={user} />
        ) : null;
      case "pas":
        return activeFacility ? (
          <PASPortal
            facility={activeFacility}
            user={user}
            systemMessages={facilityMessages}
            onDismissMessage={dismissMessage}
          />
        ) : null;
      case "patient_aftercare":
        return activeFacility ? (
          <PatientAftercarePortal
            facility={activeFacility}
            user={user}
            systemMessages={facilityMessages}
            onDismissMessage={dismissMessage}
          />
        ) : null;
      case "construction":
        return activeFacility ? (
          <BuildIQPortal facility={activeFacility} />
        ) : null;
      case "telegram_bot":
        return activeFacility ? (
          <TelegramBotPortal
            facility={activeFacility}
            user={user}
            systemMessages={facilityMessages}
            onDismissMessage={dismissMessage}
          />
        ) : null;
      case "voip":
        return activeFacility ? (
          <VoiceIQPortal
            facility={activeFacility}
            user={user}
            systemMessages={facilityMessages}
            onDismissMessage={dismissMessage}
          />
        ) : null;
      case "article":
        return activeFacility ? (
          <ArticleGeneratorPortal facility={activeFacility} user={user} />
        ) : null;
      case "email_intelligence":
        return activeFacility ? (
          <EmailIntelligencePortal
            facility={activeFacility}
            user={user}
            pendingCid={pendingEmailCid}
          />
        ) : null;
      case "marketing":
        return activeFacility ? (
          <MarketingPortal facility={activeFacility} user={user} />
        ) : null;
      case "facility-admin":
        return activeFacility ? (
          <AdminPanel facility={activeFacility} user={user} />
        ) : null;
      case "admin-rag-config":
        return <AdminFacilityConfig />;
      default:
        return (
          <Dashboard facilities={facilities} onNavigate={navigateToService} />
        );
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Top nav */}
      <nav className="bg-white shadow-sm border-b border-slate-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => {
              setPage("dashboard");
              setActiveFacility(null);
            }}
            className="flex items-center space-x-2 text-slate-800 hover:text-blue-600"
          >
            <img
              src="/logo-white.png"
              alt="Hyve Applied Intelligence"
              className="h-7"
              style={{
                filter: "brightness(0) invert(20%)",
              }}
            />
          </button>
          {activeFacility && (
            <>
              <span className="text-slate-300">/</span>
              <button
                onClick={() => {
                  setActiveFacility(null);
                  setPage("dashboard");
                }}
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                title="Back to home"
              >
                {activeFacility.facility_name}
              </button>
              <span className="text-slate-300">/</span>
              <span className="text-sm font-medium text-slate-800">
                {page === "facility-admin"
                  ? "Admin"
                  : SERVICE_META[page]?.label || "Dashboard"}
              </span>
            </>
          )}
        </div>
        <div className="flex items-center space-x-4">
          <span className="text-sm text-slate-500">
            {user.first_name} {user.last_name}
          </span>
          {activeFacility && isActiveFacilityAdmin && (
            <button
              onClick={() => setPage("facility-admin")}
              className={`flex items-center px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                page === "facility-admin"
                  ? "bg-blue-600 text-white"
                  : "text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              }`}
              title="Facility Admin"
            >
              <Shield className="w-4 h-4 mr-1.5" />
              Admin
            </button>
          )}
          {user.role === "admin" && (
            <button
              onClick={() => {
                setPage("admin-rag-config");
                setActiveFacility(null);
              }}
              className={`p-2 rounded-lg hover:bg-slate-50 ${page === "admin-rag-config" ? "text-blue-600" : "text-slate-400 hover:text-slate-600"}`}
              title="Admin: RAG config"
            >
              <Settings className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={handleLogout}
            className="p-2 text-slate-400 hover:text-red-500 rounded-lg hover:bg-slate-50"
            title="Sign out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </nav>

      {/* Global system message banners */}
      {globalMessages.map((msg) => (
        <SystemBanner key={msg.id} message={msg} onDismiss={dismissMessage} />
      ))}

      {/* Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">{renderPage()}</main>

      <ToastHost />
    </div>
  );
}

export default App;
